// Pure derive helpers — all numbers come from real state (no hard-code).
// Recurring rules (e.g. salary) are materialized into each month's ledger.
// Rollup helpers (project progress, goal rollup) live in rollup-derive.ts and are re-exported here.
//
// D5: Transfers (txn.toAccountId set) are excluded from income/expense and budget calculations.
//     They are money movement between wallets, not P&L events.
// D7: Budget rollover — when budget.rollover = true, carry unused/over balance across ALL prior months.
//     True envelope logic lives in budget-rollover-derive.ts (B5). bento-derive delegates to it.

import { todayIso } from "../lib/date";
import type { LifeOsStateWithRecurring, RecurringRule } from "../lib/life-os-contract";
import type { Budget, Transaction } from "../types";
import { budgetEnvelope, buildSpendIndex, type SpendIndex } from "./budget-rollover-derive";
import { entryAmountToVnd } from "./currency-derive";
import { recurringOccurrenceDates } from "./recurring-occurrence-derive";

export { deriveProjectProgress, deriveProjectTaskCounts, deriveGoalRollup, goalDisplayPercent } from "./rollup-derive";
export type { GoalRollupResult } from "./rollup-derive";

export const monthKeyOf = (date: string) => date.slice(0, 7);
export type LedgerEntry = Transaction & { auto?: boolean; ruleId?: string };

/** Returns true when this transaction is a TRANSFER (D5). Transfers are excluded from P&L. */
export const isTransfer = (t: Pick<Transaction, "toAccountId">) => Boolean(t.toAccountId);

/**
 * Materialize recurring rules that have "occurred" within the given month.
 * D49: lịch phát sinh (monthly/weekly/quarterly/yearly + endOfMonth) do
 * recurringOccurrenceDates lo — 1 rule weekly có thể sinh NHIỀU entry/tháng.
 * Id: 1 occurrence → `auto_<rule>_<monthKey>` (giữ back-compat); nhiều → kèm ngày.
 */
export function recurringForMonth(rules: RecurringRule[], monthKey: string, today: string): LedgerEntry[] {
  return rules.flatMap((r) => {
    const dates = recurringOccurrenceDates(r, monthKey, today);
    return dates.map((date) => ({
      id: dates.length > 1 ? `auto_${r.id}_${date}` : `auto_${r.id}_${monthKey}`,
      ruleId: r.id,
      auto: true,
      date,
      type: r.type,
      category: r.category,
      amount: r.amount,
      note: r.note ?? "Định kỳ",
      // Preserve accountId for downstream use (account-derive reads it)
      accountId: r.accountId,
    }));
  });
}

/**
 * Real transactions (excluding transfers) + materialized recurring for a month.
 * D46: amount của mỗi entry được QUY ĐỔI sang VND theo tiền tệ ví của nó, để mọi
 * tổng P&L (thu/chi, donut, tỉ lệ tiết kiệm, dòng tiền) nhất quán một đơn vị.
 * State toàn VND → pass-through, không đổi hành vi cũ.
 */
export function monthLedger(state: LifeOsStateWithRecurring, monthKey: string, today = todayIso()): LedgerEntry[] {
  // D5: exclude transfers from the P&L ledger
  const real = state.transactions.filter(
    (t) => monthKeyOf(t.date) === monthKey && !isTransfer(t),
  );
  const entries = [...real, ...recurringForMonth(state.recurring, monthKey, today)];
  return entries.map((e) => ({
    ...e,
    amount: entryAmountToVnd(e.amount, e.accountId, state.accounts, state.currencyRates),
  }));
}

const sum = (rows: { amount: number }[]) => rows.reduce((s, t) => s + t.amount, 0);

// ─── budget rollover (D7) — delegates to budget-rollover-derive (B5) ─────────

/** D49 ngưỡng cảnh báo mặc định khi budget chưa đặt riêng (đã dùng ≥ % này → cảnh báo). */
export const DEFAULT_BUDGET_ALERT_THRESHOLD = 80;

export type BudgetStatus = {
  budget: Budget;
  spent: number;
  /** Effective limit after applying rollover carry (= monthlyLimit when rollover disabled). */
  effectiveLimit: number;
  percent: number;
  over: boolean;
  remaining: number;
  /** Cumulative carry applied this month (positive = surplus, negative = deficit). B5: full history. */
  rollover: number;
  /** D49: % hạn mức đã đặt cho cảnh báo (budget.alertThreshold ?? mặc định). */
  alertThreshold: number;
  /** D49: đã chạm/vượt ngưỡng cảnh báo nhưng CHƯA vượt hạn mức → nhắc "sắp hết". */
  alerting: boolean;
};

// F8: one SpendIndex shared by every budget of a derive pass, memoized on the
// IDENTITY of the state slices (state arrays are immutable — replaced on each
// mutation — so === comparison is a correct cache key). Collapses the old
// O(budgets × months × transactions) rollover walk to O(transactions) + lookups.
let spendIndexCache: {
  transactions: unknown;
  recurring: unknown;
  accounts: unknown;
  rates: unknown;
  today: string;
  index: SpendIndex;
} | null = null;

function sharedSpendIndex(state: LifeOsStateWithRecurring, today: string): SpendIndex {
  const c = spendIndexCache;
  if (
    c &&
    c.transactions === state.transactions &&
    c.recurring === state.recurring &&
    c.accounts === state.accounts &&
    c.rates === state.currencyRates &&
    c.today === today
  ) {
    return c.index;
  }
  const index = buildSpendIndex(state.transactions, state.recurring, today, state.accounts, state.currencyRates);
  spendIndexCache = {
    transactions: state.transactions,
    recurring: state.recurring,
    accounts: state.accounts,
    rates: state.currencyRates,
    today,
    index,
  };
  return index;
}

export function budgetStatuses(
  state: LifeOsStateWithRecurring,
  monthKey: string,
  today = todayIso(),
): BudgetStatus[] {
  const index = sharedSpendIndex(state, today);
  return state.budgets
    .map((budget) => {
      // B5: delegate to true envelope (full history walk for rollover; O(1) for non-rollover)
      const envelope = budgetEnvelope(
        budget,
        state.transactions,
        state.recurring,
        monthKey,
        today,
        state.accounts,
        state.currencyRates,
        index,
      );

      const { spent, carryIn: rollover } = envelope;
      const effectiveLimit = budget.monthlyLimit + rollover;
      const percent = Math.min(100, Math.round((spent / Math.max(effectiveLimit, 1)) * 100));
      const over = spent > effectiveLimit;
      const alertThreshold = budget.alertThreshold ?? DEFAULT_BUDGET_ALERT_THRESHOLD;
      // Cảnh báo "sắp hết" = đã đạt ngưỡng nhưng chưa thực sự vượt (vượt rồi thì
      // báo đỏ riêng), tính theo % thực (không kẹp 100) để vẫn báo khi sát hạn mức.
      const realPercent = (spent / Math.max(effectiveLimit, 1)) * 100;
      return {
        budget,
        spent,
        effectiveLimit,
        percent,
        over,
        remaining: effectiveLimit - spent,
        rollover,
        alertThreshold,
        alerting: !over && realPercent >= alertThreshold,
      };
    })
    .sort((a, b) => Number(b.over) - Number(a.over) || b.percent - a.percent);
}

export function deriveToday(state: LifeOsStateWithRecurring) {
  const today = todayIso();
  const monthKey = monthKeyOf(today);
  const open = state.tasks.filter((t) => t.status !== "done");
  const overdue = open.filter((t) => t.dueDate < today);
  const dueToday = open.filter((t) => t.dueDate === today);
  // Note: today's focus/MIT list now lives in today-rituals-derive (suggestMits /
  // todaysFocusTasks); deriveToday no longer computes a priority-sorted focus list.

  const ledger = monthLedger(state, monthKey, today);
  const income = sum(ledger.filter((t) => t.type === "income"));
  const expense = sum(ledger.filter((t) => t.type === "expense"));
  const spentToday = sum(ledger.filter((t) => t.type === "expense" && t.date === today));
  const monthBudget = state.budgets.reduce((s, b) => s + b.monthlyLimit, 0);
  const statuses = budgetStatuses(state, monthKey, today);

  return {
    today,
    monthKey,
    overdue,
    dueToday,
    habitsDoneToday: state.habits.filter((h) => h.completions.includes(today)).length,
    habitsTotal: state.habits.length,
    income,
    expense,
    balance: income - expense,
    spentToday,
    monthBudget,
    budgetRemaining: monthBudget - expense,
    overBudget: statuses.filter((s) => s.over),
    budgetAlerts: statuses.filter((s) => s.alerting), // D49: sắp chạm hạn mức (chưa vượt)
    todayEvents: state.events.filter((e) => e.date === today).sort((a, b) => a.time.localeCompare(b.time)),
    activeProjects: state.projects.filter((p) => p.status === "active"),
  };
}
